<script setup lang="ts">
  import { onMounted, ref } from 'vue'
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import { Switch } from '@/components/ui/switch'
  import InputField from '@/components/admin/form/InputField.vue'
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'
  import {
    OrganisationsService,
    type CustomerOrganisation as Organisation,
    type PatchedCustomerOrganisationRequest
  } from '@/types/generated'

  const org = ref<Organisation>()

  onMounted(async () => {
    const response = await OrganisationsService.organisationsRetrieve({
      uuid: 'current'
    })
    org.value = response as Organisation
  })

  const handleSave = async (values: Partial<Organisation>) => {
    await OrganisationsService.organisationsPartialUpdate({
      uuid: 'current',
      requestBody: values as PatchedCustomerOrganisationRequest
    })
  }

  const handleSwitchChange = (value: boolean) => {
    org.value.send_document_email_reminders = value
    handleSave({
      send_document_email_reminders: value
    })
  }
</script>
<template>
  <SettingsPage>
    <AutoSavingForm
      v-if="org"
      class=""
      @save="handleSave"
    >
      <div class="mb-3 block font-medium">Kanzlei Benachrichtigungen</div>
      <div class="">
        <CheckboxField
          v-model="org.receive_new_case_mail"
          name="receive_new_case_mail"
          label="E-Mail erhalten, wenn eine neue Anfrage erstellt wird"
        />
        <CheckboxField
          v-model="org.receive_document_signed_mail"
          name="receive_document_signed_mail"
          label="E-Mail erhalten, wenn ein Dokument abgeschlossen wird"
        />
        <CheckboxField
          v-model="org.receive_questionnaire_closed_mail"
          name="receive_questionnaire_closed_mail"
          label="E-Mail erhalten, wenn ein Fragebogen abgeschlossen wird"
        />
        <CheckboxField
          v-model="org.receive_new_case_email_mail"
          name="receive_new_case_email_mail"
          label="E-Mail erhalten, wenn eine neue E-Mail zu einer Anfrage eingeht"
        />
        <CheckboxField
          v-model="org.receive_new_appointment_mail"
          name="receive_new_appointment_mail"
          label="E-Mail erhalten, wenn ein neuer Terminvorschlag erstellt wird"
        />
        <CheckboxField
          v-model="org.receive_clerk_change_mail"
          name="receive_clerk_change_mail"
          label="E-Mail erhalten, wenn ein Sachbearbeiter zugewiesen wird"
        />
      </div>

      <div class="mb-3 mt-6 block font-medium">Mandanten Benachrichtigungen</div>
      <p class="mb-4 text-sm text-gray-600">
        Wählen Sie aus, ob und in welchem Intervall Sie Mandanten automatisiert per E-Mail an das
        Ausfüllen der versendeten Dokumente und Fragebögen erinnern möchten. Diese Benachrichtigung
        wird nur versendet, wenn der Mandant die Dokumente bzw. Fragebögen noch nicht bearbeitet
        hat.
      </p>
      <div class="rounded-lg border border-gray-300 p-4">
        <div class="flex items-center">
          <span class="mr-2 text-sm font-medium text-gray-700">
            Automatische E-Mail Erinnerungen aktiv
          </span>
          <Switch
            v-model="org.send_document_email_reminders"
            :checked="org.send_document_email_reminders"
            class="data-[state=checked]:bg-purple-700 data-[state=unchecked]:bg-gray-200"
            @update:checked="handleSwitchChange"
          />
        </div>
        <div
          v-if="org.send_document_email_reminders"
          class="my-4 w-full border-b border-gray-200"
        ></div>
        <div
          v-if="org.send_document_email_reminders"
          class="mt-4"
        >
          <label
            for="reminder-days"
            class="mb-1 block text-sm font-medium text-gray-700"
          >
            Nach wie vielen Tagen soll eine automatisierte E-Mail-Erinnerung versendet werden?
          </label>
          <div class="relative w-24">
            <InputField
              id="reminder-days"
              v-model="org.send_document_email_reminders_every_x_days"
              type="number"
              name="send_document_email_reminders_every_x_days"
              class="w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-indigo-500
                focus:outline-none focus:ring-indigo-500 sm:text-sm"
            />
          </div>
        </div>
      </div>
    </AutoSavingForm>
  </SettingsPage>
</template>
